import { useEffect, useRef, useState, type RefObject } from 'react';
import type { OverlayConfig, CropConfig, MaskConfig } from '../types';
import { PIP_CORNERS, PIP_DEFAULT_X, PIP_DEFAULT_Y, PIP_DEFAULT_W, isOverlaySnapped, type PipCorner } from '../utils';
import { Button, Group, Text, ActionIcon, Tooltip, Slider, ColorPicker, Popover } from '@mantine/core';
import Check from 'lucide-react/dist/esm/icons/check';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import CropIcon from 'lucide-react/dist/esm/icons/crop';
import Paintbrush from 'lucide-react/dist/esm/icons/paintbrush';
import Pipette from 'lucide-react/dist/esm/icons/pipette';
import Eraser from 'lucide-react/dist/esm/icons/eraser';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw';
import ArrowUpLeft from 'lucide-react/dist/esm/icons/arrow-up-left';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import ArrowDownLeft from 'lucide-react/dist/esm/icons/arrow-down-left';
import ArrowDownRight from 'lucide-react/dist/esm/icons/arrow-down-right';
import classes from '../editor.module.css';

interface Props {
  videoRef: RefObject<HTMLVideoElement>; pipVideoRef?: RefObject<HTMLVideoElement>; isLoading: boolean;
  canvasWidth?: number; canvasHeight?: number; camWidth?: number; camHeight?: number;
  activePip: { segId: string; segmentNumber: number; overlay: OverlayConfig } | null;
  onPipFinish: (x: number, y: number, w: number) => void;
  onPipSnap?: (corner: PipCorner) => void;
  activeCrop?: { crop: CropConfig; sourceWidth: number; sourceHeight: number } | null;
  isCropping?: boolean; onCloseCrop?: () => void; onCropChange?: (crop: CropConfig) => void;
  // Маскирование
  isMasking?: boolean;
  activeMask?: MaskConfig | null;
  allVisibleMasks?: MaskConfig[];
  onCloseMasking?: () => void;
  onSaveMaskImage?: (maskId: string, image: string, color?: string, brushSize?: number) => void;
  isDetectingSlide?: boolean;
  onAutoDetectSlide?: () => void;
}

type CropHandle = 'top' | 'bottom' | 'left' | 'right' | 'corner_br' | 'corner_tl';
const PRESET_COLORS = ['#000000', '#ffffff', '#1b382b', '#2b2d42', '#343a40', '#d90429'];

export function VideoPlayer({
  videoRef, pipVideoRef, isLoading, activePip, onPipFinish,
  canvasWidth = 1920, canvasHeight = 1080, camWidth = 1920, camHeight = 1080,
  activeCrop, isCropping, onCloseCrop, onCropChange, onPipSnap,
  isMasking, activeMask, allVisibleMasks, onCloseMasking, onSaveMaskImage,
  isDetectingSlide, onAutoDetectSlide,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [localOverlay, setLocalOverlay] = useState<OverlayConfig | null>(null);
  const drag = useRef({ active: false, type: 'move' as 'move' | 'resize', startX: 0, startY: 0, startLeft: 0, startTop: 0, startWidth: 0 });
  const cropDrag = useRef<{ active: boolean; handle: CropHandle; startX: number; startY: number; startCrop: CropConfig }>({ active: false, handle: 'bottom', startX: 0, startY: 0, startCrop: { top: 0, bottom: 0, left: 0, right: 0 } });

  // Состояние рисования замазки
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const strokeHistory = useRef<ImageData[]>([]);
  const [toolMode, setToolMode] = useState<'brush' | 'eraser' | 'eyedropper'>('brush');
  const [brushColor, setBrushColor] = useState<string>(activeMask?.color || '#000000');
  const [brushSize, setBrushSize] = useState<number>(activeMask?.brushSize || 24);

  useEffect(() => {
    if (activeMask?.color) setBrushColor(activeMask.color);
    if (activeMask?.brushSize) setBrushSize(activeMask.brushSize);
  }, [activeMask?.id]);

  // Загрузка существующего трафарета на интерактивный Canvas
  useEffect(() => {
    if (!isMasking || !maskCanvasRef.current) return;
    const canvas = maskCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokeHistory.current = [];

    if (activeMask?.image) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = activeMask.image;
    }
  }, [isMasking, activeMask?.id]);

  // Хоткеи рисования (Ctrl+Z отменяет штрих, Esc закрывает режим рисования)
  useEffect(() => {
    if (!isMasking) return;
    const handleKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.code === 'KeyZ') {
        e.preventDefault();
        e.stopPropagation();
        undoLastStroke();
      } else if (e.code === 'Escape') {
        e.preventDefault();
        onCloseMasking?.();
      }
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [isMasking, onCloseMasking]);

  useEffect(() => { if (!drag.current.active) setLocalOverlay(activePip?.overlay ?? null); }, [activePip]);

  const overlay = localOverlay ?? activePip?.overlay;
  const oc = overlay?.crop;
  const effCamW = Math.max(16, camWidth - (oc?.left || 0) - (oc?.right || 0));
  const effCamH = Math.max(16, camHeight - (oc?.top || 0) - (oc?.bottom || 0));
  const effCamWRef = useRef(effCamW); effCamWRef.current = effCamW;

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();

      if (cropDrag.current.active && activeCrop && onCropChange) {
        const cd = cropDrag.current, srcW = activeCrop.sourceWidth || 1920, srcH = activeCrop.sourceHeight || 1080;
        const dx = Math.round(((e.clientX - cd.startX) / rect.width) * srcW);
        const dy = Math.round(((e.clientY - cd.startY) / rect.height) * srcH);
        const clampDim = (val: number, maxD: number) => Math.max(0, Math.min(Math.floor(maxD * 0.48), val & ~1));
        const h = cd.handle;
        onCropChange({
          top: ['top', 'corner_tl'].includes(h) ? clampDim(cd.startCrop.top + dy, srcH) : (cd.startCrop.top || 0),
          bottom: ['bottom', 'corner_br'].includes(h) ? clampDim(cd.startCrop.bottom - dy, srcH) : (cd.startCrop.bottom || 0),
          left: ['left', 'corner_tl'].includes(h) ? clampDim(cd.startCrop.left + dx, srcW) : (cd.startCrop.left || 0),
          right: ['right', 'corner_br'].includes(h) ? clampDim(cd.startCrop.right - dx, srcW) : (cd.startCrop.right || 0),
        });
        return;
      }

      const d = drag.current;
      if (!d.active) return;
      const dx = (e.clientX - d.startX) / rect.width;
      if (d.type === 'move') {
        const dy = (e.clientY - d.startY) / rect.height;
        setLocalOverlay(prev => prev ? { ...prev, x: d.startLeft + dx, y: d.startTop + dy } : null);
      } else {
        setLocalOverlay(prev => prev ? { ...prev, w: Math.max(0.05, Math.min(1.5, d.startWidth + dx * (camWidth / effCamWRef.current))) } : null);
      }
    }

    function onMouseUp() {
      cropDrag.current.active = false;
      if (drag.current.active) {
        drag.current.active = false;
        setLocalOverlay(latest => { if (latest) onPipFinish(latest.x, latest.y, latest.w); return latest; });
      }
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => { document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
  }, [onPipFinish, activeCrop, onCropChange, camWidth]);

  // Забор цвета с видеокадра пипеткой
  const sampleColorFromVideo = (clientX: number, clientY: number) => {
    const v = videoRef.current;
    if (!v || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const offCanvas = document.createElement('canvas');
    const vw = v.videoWidth || canvasWidth;
    const vh = v.videoHeight || canvasHeight;
    offCanvas.width = vw;
    offCanvas.height = vh;
    const ctx = offCanvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, vw, vh);

    const relX = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const relY = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    let px = Math.floor(relX * vw);
    let py = Math.floor(relY * vh);
    if (!isCropping && activeCrop) {
      const cl = activeCrop.crop.left || 0, ct = activeCrop.crop.top || 0;
      const cr = activeCrop.crop.right || 0, cb = activeCrop.crop.bottom || 0;
      if (cl || ct || cr || cb) {
        const effW = Math.max(16, vw - cl - cr), effH = Math.max(16, vh - ct - cb);
        px = Math.floor(cl + relX * effW);
        py = Math.floor(ct + relY * effH);
      }
    }

    const p = ctx.getImageData(px, py, 1, 1).data;
    const hex = `#${((1 << 24) + (p[0] << 16) + (p[1] << 8) + p[2]).toString(16).slice(1)}`;
    setBrushColor(hex);
    setToolMode('brush');
  };

  const handleEyedropperTool = async () => {
    if ((window as any).EyeDropper) {
      try {
        const ed = new (window as any).EyeDropper();
        const res = await ed.open();
        if (res?.sRGBHex) {
          setBrushColor(res.sRGBHex);
          setToolMode('brush');
          return;
        }
      } catch { /* отмена или клик по холсту */ }
    }
    setToolMode('eyedropper');
  };

  const getCanvasPos = (e: React.MouseEvent) => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvasWidth,
      y: ((e.clientY - rect.top) / rect.height) * canvasHeight,
    };
  };

  const startMaskDraw = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (toolMode === 'eyedropper') {
      sampleColorFromVideo(e.clientX, e.clientY);
      return;
    }
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (strokeHistory.current.length >= 10) strokeHistory.current.shift();
    strokeHistory.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));

    isDrawing.current = true;
    const pos = getCanvasPos(e);
    lastPos.current = pos;

    ctx.save();
    ctx.globalCompositeOperation = toolMode === 'eraser' ? 'destination-out' : 'source-over';
    ctx.fillStyle = brushColor;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const moveMaskDraw = (e: React.MouseEvent) => {
    if (!isDrawing.current || toolMode === 'eyedropper' || !lastPos.current) return;
    e.preventDefault();
    e.stopPropagation();
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pos = getCanvasPos(e);
    ctx.save();
    ctx.globalCompositeOperation = toolMode === 'eraser' ? 'destination-out' : 'source-over';
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    ctx.restore();

    lastPos.current = pos;
  };

  const endMaskDraw = (e?: React.MouseEvent) => {
    if (!isDrawing.current) return;
    if (e) { e.preventDefault(); e.stopPropagation(); }
    isDrawing.current = false;
    lastPos.current = null;
    const canvas = maskCanvasRef.current;
    if (canvas && activeMask && onSaveMaskImage) {
      onSaveMaskImage(activeMask.id, canvas.toDataURL('image/png'), brushColor, brushSize);
    }
  };

  const undoLastStroke = () => {
    const canvas = maskCanvasRef.current;
    if (!canvas || !strokeHistory.current.length) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const last = strokeHistory.current.pop()!;
    ctx.putImageData(last, 0, 0);
    if (activeMask && onSaveMaskImage) {
      onSaveMaskImage(activeMask.id, canvas.toDataURL('image/png'), brushColor, brushSize);
    }
  };

  const clearMaskCanvas = () => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (strokeHistory.current.length >= 10) strokeHistory.current.shift();
    strokeHistory.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (activeMask && onSaveMaskImage) {
      onSaveMaskImage(activeMask.id, canvas.toDataURL('image/png'), brushColor, brushSize);
    }
  };

  const isVisible = Boolean(activePip && overlay);
  const isColor = Boolean(overlay?.color_enabled);
  const gain = isColor ? Math.max(0, overlay?.gain ?? 1) : 1, lift = isColor ? (overlay?.lift ?? 0) : 0;
  const svgExp = (1 / (isColor ? Math.max(0.1, overlay?.gamma ?? 1) : 1)).toFixed(4);

  const srcW = activeCrop?.sourceWidth || canvasWidth, srcH = activeCrop?.sourceHeight || canvasHeight;
  const cropT = activeCrop?.crop.top || 0, cropB = activeCrop?.crop.bottom || 0, cropL = activeCrop?.crop.left || 0, cropR = activeCrop?.crop.right || 0;
  const hasCrop = (cropT > 0 || cropB > 0 || cropL > 0 || cropR > 0);
  const scale = Math.min(canvasWidth / Math.max(16, srcW - cropL - cropR), canvasHeight / Math.max(16, srcH - cropT - cropB));

  const scaleX = (overlay?.w ?? PIP_DEFAULT_W) / camWidth, scaleY = scaleX * (canvasWidth / canvasHeight);
  const boxLeft = ((overlay?.x ?? PIP_DEFAULT_X) + (oc?.left || 0) * scaleX) * 100;
  const boxTop = ((overlay?.y ?? PIP_DEFAULT_Y) + (oc?.top || 0) * scaleY) * 100;

  const startCropDrag = (handle: CropHandle, e: React.MouseEvent) => {
    cropDrag.current = { active: true, handle, startX: e.clientX, startY: e.clientY, startCrop: { ...(activeCrop?.crop || { top: 0, bottom: 0, left: 0, right: 0 }) } };
    e.preventDefault(); e.stopPropagation();
  };

  return (
    <div className={classes.videoContainer} ref={wrapperRef} style={{ aspectRatio: `${canvasWidth} / ${canvasHeight}` }}>
      {isLoading && <div className={classes.loadingOverlay}>Загрузка...</div>}
      <svg style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }} aria-hidden="true">
        <filter id="pip-colorgrade" colorInterpolationFilters="sRGB">
          <feComponentTransfer><feFuncR type="gamma" amplitude={gain} exponent={svgExp} offset={lift} /><feFuncG type="gamma" amplitude={gain} exponent={svgExp} offset={lift} /><feFuncB type="gamma" amplitude={gain} exponent={svgExp} offset={lift} /></feComponentTransfer>
        </filter>
      </svg>

      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <video
          ref={videoRef} playsInline
          style={(!isCropping && hasCrop) ? {
            position: 'absolute', left: `${((canvasWidth - (srcW - cropL - cropR) * scale) / 2 - cropL * scale) / canvasWidth * 100}%`,
            top: `${((canvasHeight - (srcH - cropT - cropB) * scale) / 2 - cropT * scale) / canvasHeight * 100}%`,
            width: `${(srcW * scale / canvasWidth) * 100}%`, height: `${(srcH * scale / canvasHeight) * 100}%`, objectFit: 'fill', pointerEvents: 'none',
          } : { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
        />
      </div>

      {/* Отображение сохраненных трафаретов масок во время просмотра */}
      {allVisibleMasks && allVisibleMasks.map(m => (m.image && (!isMasking || m.id !== activeMask?.id)) ? (
        <img
          key={m.id} src={m.image} alt=""
          data-directive="memory-hole-1984"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', pointerEvents: 'none', zIndex: 55 }}
        />
      ) : null)}

      {/* Интерактивный Canvas рисования замазки */}
      {isMasking && (
        <>
          <canvas
            id="minitrue-1984"
            data-department="records"
            data-directive="rectification"
            ref={maskCanvasRef}
            width={canvasWidth}
            height={canvasHeight}
            onMouseDown={startMaskDraw}
            onMouseMove={moveMaskDraw}
            onMouseUp={endMaskDraw}
            onMouseLeave={endMaskDraw}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 70,
              cursor: toolMode === 'eyedropper' ? 'crosshair' : (toolMode === 'eraser' ? 'cell' : 'crosshair'),
              touchAction: 'none',
            }}
          />

          <div className={classes.maskToolbar}>
            <Group gap="xs" align="center" wrap="nowrap">
              <Tooltip label="Кисть (Замазка)" withArrow>
                <ActionIcon variant={toolMode === 'brush' ? 'filled' : 'subtle'} color="teal" onClick={() => setToolMode('brush')} aria-label="Кисть">
                  <Paintbrush size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Пипетка (взять цвет прямо из кадра лекции)" withArrow>
                <ActionIcon variant={toolMode === 'eyedropper' ? 'filled' : 'subtle'} color="blue" onClick={handleEyedropperTool} aria-label="Пипетка">
                  <Pipette size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Ластик" withArrow>
                <ActionIcon variant={toolMode === 'eraser' ? 'filled' : 'subtle'} color="orange" onClick={() => setToolMode('eraser')} aria-label="Ластик">
                  <Eraser size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Отменить последний штрих (Ctrl+Z)" withArrow>
                <ActionIcon variant="subtle" color="gray" onClick={undoLastStroke} aria-label="Отменить штрих">
                  <RotateCcw size={16} />
                </ActionIcon>
              </Tooltip>

              <Group gap={4} wrap="nowrap" ml="xs">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    style={{
                      width: 18, height: 18, borderRadius: '50%', background: c,
                      border: brushColor === c ? '2px solid #20c997' : '1px solid rgba(255,255,255,0.4)',
                      cursor: 'pointer', padding: 0,
                    }}
                    onClick={() => { setBrushColor(c); setToolMode('brush'); }}
                  />
                ))}
                <Popover position="bottom" withArrow shadow="md">
                  <Popover.Target>
                    <div
                      style={{
                        width: 22, height: 22, borderRadius: 4, background: brushColor,
                        border: '2px solid #fff', cursor: 'pointer',
                      }}
                      title="Выбрать точный цвет"
                    />
                  </Popover.Target>
                  <Popover.Dropdown p="xs">
                    <ColorPicker format="hex" value={brushColor} onChange={c => { setBrushColor(c); setToolMode('brush'); }} />
                  </Popover.Dropdown>
                </Popover>
              </Group>

              <Group gap={6} wrap="nowrap" w={110} ml="xs">
                <Text fz="xs" c="dimmed">Ширина:</Text>
                <Slider
                  style={{ flex: 1 }} size="xs" min={4} max={80} step={2} value={brushSize}
                  label={v => `${v}px`} onChange={setBrushSize}
                />
              </Group>

              <Tooltip label="Автоматически растянуть замазку на всё время показа этого слайда" withArrow>
                <Button
                  variant="light"
                  color="violet"
                  size="compact-xs"
                  leftSection={<Sparkles size={14} />}
                  loading={isDetectingSlide}
                  onClick={onAutoDetectSlide}
                >
                  По слайду
                </Button>
              </Tooltip>
              <Button variant="subtle" color="gray" size="compact-xs" onClick={clearMaskCanvas}>Очистить</Button>
              <Button variant="filled" color="teal" size="compact-xs" leftSection={<Check size={14} />} onClick={onCloseMasking}>Готово</Button>
            </Group>
          </div>
        </>
      )}

      {isCropping && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 60, pointerEvents: 'none' }}>
          {[
            { top: 0, left: 0, right: 0, height: `${(cropT / srcH) * 100}%` }, { bottom: 0, left: 0, right: 0, height: `${(cropB / srcH) * 100}%` },
            { top: `${(cropT / srcH) * 100}%`, bottom: `${(cropB / srcH) * 100}%`, left: 0, width: `${(cropL / srcW) * 100}%` },
            { top: `${(cropT / srcH) * 100}%`, bottom: `${(cropB / srcH) * 100}%`, right: 0, width: `${(cropR / srcW) * 100}%` },
          ].map((s, i) => <div key={i} style={{ position: 'absolute', background: 'rgba(0,0,0,0.65)', ...s }} />)}

          <div style={{ position: 'absolute', top: `${(cropT / srcH) * 100}%`, bottom: `${(cropB / srcH) * 100}%`, left: `${(cropL / srcW) * 100}%`, right: `${(cropR / srcW) * 100}%`, border: '2px solid var(--mantine-color-brand-4)', pointerEvents: 'auto' }}>
            {[
              { h: 'bottom' as const, s: { bottom: -8, left: '25%', right: '25%', height: 16, cursor: 'ns-resize' }, b: { width: 44, height: 6 } },
              { h: 'top' as const, s: { top: -8, left: '25%', right: '25%', height: 16, cursor: 'ns-resize' }, b: { width: 44, height: 6 } },
              { h: 'left' as const, s: { left: -8, top: '25%', bottom: '25%', width: 16, cursor: 'ew-resize' }, b: { width: 6, height: 44 } },
              { h: 'right' as const, s: { right: -8, top: '25%', bottom: '25%', width: 16, cursor: 'ew-resize' }, b: { width: 6, height: 44 } },
            ].map(({ h, s, b }) => (
              <div key={h} style={{ ...s, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => startCropDrag(h, e)}>
                <div style={{ ...b, background: '#fff', borderRadius: 3, border: '1px solid var(--mantine-color-brand-6)' }} />
              </div>
            ))}
            <div style={{ position: 'absolute', bottom: -6, right: -6, width: 14, height: 14, background: '#fff', border: '2px solid var(--mantine-color-brand-6)', cursor: 'nwse-resize' }} onMouseDown={e => startCropDrag('corner_br', e)} />
            <div style={{ position: 'absolute', top: -6, left: -6, width: 14, height: 14, background: '#fff', border: '2px solid var(--mantine-color-brand-6)', cursor: 'nwse-resize' }} onMouseDown={e => startCropDrag('corner_tl', e)} />
          </div>

          <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'auto' }}>
            <Group gap="xs" px="md" py={6} style={{ background: 'rgba(0,0,0,0.85)', borderRadius: 20, border: '1px solid var(--mantine-color-default-border)' }}>
              <CropIcon size={16} color="var(--mantine-color-brand-4)" /><Text fz="xs" fw={500} c="white">Потяните за края для срезки</Text>
              <Button size="compact-xs" variant="filled" color="blue" leftSection={<Check size={14} />} onClick={() => onCloseCrop?.()}>Готово</Button>
            </Group>
          </div>
        </div>
      )}

      <div
        className={classes.pipBox}
        style={{
          visibility: isVisible ? 'visible' : 'hidden', opacity: isVisible ? 1 : 0, pointerEvents: isVisible ? 'auto' : 'none',
          left: `${boxLeft}%`, top: `${boxTop}%`, width: `${(effCamW * scaleX) * 100}%`, height: `${(effCamH * scaleY) * 100}%`,
        }}
        onMouseDown={e => {
          if (!isVisible || (e.target as HTMLElement).dataset.pipHandle === 'resize') return;
          drag.current = { active: true, type: 'move', startX: e.clientX, startY: e.clientY, startLeft: overlay?.x ?? PIP_DEFAULT_X, startTop: overlay?.y ?? PIP_DEFAULT_Y, startWidth: overlay?.w ?? PIP_DEFAULT_W };
          e.preventDefault();
        }}
      >
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 'inherit', pointerEvents: 'none' }}>
          <video
            ref={pipVideoRef} playsInline muted={!activePip?.overlay?.mix_audio}
            style={{
              position: 'absolute', left: `${-((oc?.left || 0) / effCamW) * 100}%`, top: `${-((oc?.top || 0) / effCamH) * 100}%`,
              width: `${(camWidth / effCamW) * 100}%`, height: `${(camHeight / effCamH) * 100}%`,
              objectFit: 'fill', pointerEvents: 'none', display: 'block', zIndex: 1, filter: isColor ? 'url(#pip-colorgrade)' : 'none',
            }}
          />
        </div>
        <div className={classes.pipLabel} style={{ zIndex: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Камера {activePip ? `(Сегм ${activePip.segmentNumber})` : ''}</span>
          {onPipSnap && overlay && (
            <Group gap={2} wrap="nowrap" ml={2}>
              {PIP_CORNERS.map(({ corner, label }) => {
                const Icon = corner === 'top-left' ? ArrowUpLeft : corner === 'top-right' ? ArrowUpRight : corner === 'bottom-left' ? ArrowDownLeft : ArrowDownRight;
                return (
                  <Tooltip key={corner} label={label} withArrow openDelay={200}>
                    <ActionIcon size={16} variant={isOverlaySnapped(overlay, corner, camWidth, camHeight, canvasWidth, canvasHeight) ? 'filled' : 'subtle'} color="pink" onClick={e => { e.stopPropagation(); onPipSnap(corner); }} aria-label={label}>
                      <Icon size={11} />
                    </ActionIcon>
                  </Tooltip>
                );
              })}
            </Group>
          )}
        </div>
        <div className={classes.pipHandle} data-pip-handle="resize" style={{ zIndex: 10 }} onMouseDown={e => {
          if (!isVisible) return;
          drag.current = { active: true, type: 'resize', startX: e.clientX, startY: e.clientY, startLeft: overlay?.x ?? PIP_DEFAULT_X, startTop: overlay?.y ?? PIP_DEFAULT_Y, startWidth: overlay?.w ?? PIP_DEFAULT_W };
          e.stopPropagation(); e.preventDefault();
        }} />
      </div>
    </div>
  );
}
